import { useState, useMemo } from 'react';
import useLayoutHook from '../../../hooks/layoutHook';
import moment from 'moment';
import Avatar from '../../../components/Avatar';
import { getFormattedAccessRight, getNameInitials } from '../../../utils/miscFunctions';
import { Drawer } from '../../../components/elements/Drawer';
import { Activity, Monitor, Clock, Info, HardDrive, Smartphone, Globe } from 'lucide-react';

interface IViewLog {
  onClose?: () => void;
  show: boolean;
  log: any;
}

const ViewLog = ({ onClose, show = false, log }: IViewLog) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(show);
  const { isMobile } = useLayoutHook();

  const deviceType = useMemo(() => {
    const agent = log?.agent?.toLowerCase() || '';
    if (agent.includes('mobile')) return { icon: <Smartphone className="w-4 h-4" />, label: 'Mobile' };
    if (agent.includes('windows') || agent.includes('mac') || agent.includes('linux')) 
      return { icon: <Monitor className="w-4 h-4" />, label: 'Desktop' };
    return { icon: <HardDrive className="w-4 h-4" />, label: 'Device' };
  }, [log?.agent]);

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    if (onClose) onClose();
  };

  return (
    <Drawer 
      isOpen={isDrawerOpen} 
      onClose={closeDrawer} 
      position="right" 
      size={isMobile ? 'full' : 'lg'} 
      height={isMobile ? 'full' : 'auto'}
      closeOnOverlayClick
      closeOnEscape
      showCloseButton={false}
      className="bg-gray-50 dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border"
    >
      <div className="h-screen overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-6 dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">

            <div className="flex items-start space-x-4">
              <Avatar
                size="lg"
                src={log.account.profileImage}
                nameInitials={getNameInitials(log.fullName)}
                className="ring-2 ring-white shadow-sm"
              />
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-darkBg-mainText">{log.fullName}</h2>
                <div className="mt-1 flex items-center text-sm text-gray-500">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {getFormattedAccessRight(log.account.accessRights)}
                  </span>
                  <span className="mx-1">•</span>
                  <span>{moment(log.createdOn).format('MMM D, YYYY [at] h:mm A')}</span>
                </div>
              </div>
            </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Activity Card */}
          <div className="bg-white shadow-sm rounded-lg p-5 border border-gray-200 dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center dark:text-darkBg-mainText">
              <Activity className="w-5 h-5 text-blue-600 mr-2" />
              Activity Details
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Action Performed</p>
                <p className="mt-1 text-sm text-gray-900 dark:text-darkBg-mainText">{log?.activity || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Timestamp</p>
                <div className="mt-1 flex items-center text-sm text-gray-900 dark:text-darkBg-mainText">
                  <Clock className="w-4 h-4 text-gray-400 mr-1.5" />
                  {moment(log?.createdOn).format('dddd, MMMM D, YYYY [at] h:mm A')}
                </div>
              </div>
            </div>
          </div>

          {/* Device & Location Card */}
          <div className="bg-white shadow-sm rounded-lg p-5 border border-gray-200 dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center dark:text-darkBg-mainText">
              <Monitor className="w-5 h-5 text-blue-600 mr-2" />
              Device & Location
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Device Type</p>
                <div className="mt-1 flex items-center text-sm text-gray-900 dark:text-darkBg-mainText">
                  {deviceType.icon}
                  <span className="ml-1.5">{deviceType.label}</span>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">IP Address</p>
                <div className="mt-1 flex items-center text-sm text-gray-900 dark:text-darkBg-mainText">
                  <Globe className="w-4 h-4 text-gray-400 mr-1.5" />
                  {log?.ipAddress || 'N/A'}
                </div>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-gray-500">User Agent</p>
                <div className="mt-1 flex items-start text-sm text-gray-900 dark:text-darkBg-mainText">
                  <Info className="w-4 h-4 text-gray-400 mr-1.5 mt-0.5 flex-shrink-0" />
                  <span className="break-all">{log?.agent || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="bg-white shadow-sm rounded-lg p-5 border border-gray-200 dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center dark:text-darkBg-mainText">
              <Info className="w-5 h-5 text-blue-600 mr-2" />
              Additional Information
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-500">Session ID</p>
                <p className="mt-1 text-sm text-gray-900 font-mono bg-gray-50 p-2 rounded dark:text-darkBg-mainText dark:bg-transparent">
                  {log?.sessionId || 'Not available'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Status</p>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1 dark:bg-transparent">
                  Completed
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mb-4 border-t border-gray-200 bg-gray-50 px-6 py-4 dark:bg-transparent dark:border-darkBg-border">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={closeDrawer}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default ViewLog;
